import isEmpty from 'lodash-es/isEmpty';
import { type PageLayoutDetail, type LayoutNode } from 'shop-ui/types';
import { ELEMENT_TYPE, LAYOUT_SECTION_NAME } from '../constants';

export class Formatter {
    layoutData: PageLayoutDetail | null;

    effectiveLayoutNodes: string[] = [];

    constructor(layoutData: PageLayoutDetail) {
        this.layoutData = layoutData;

        this.effectiveLayoutNodes = [];
    }

    parseStructure() {
        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail } = this.layoutData;

        const run = (node: LayoutNode) => {
            if (!node || typeof node !== 'object') {
                return;
            }

            this.effectiveLayoutNodes.push(node.id);

            if (node.next && Array.isArray(node.next)) {
                node.next.forEach(run);
            }
        };

        if (layoutDetail.layout) {
            if (Array.isArray(layoutDetail.layout)) {
                layoutDetail.layout.forEach(run);
            } else {
                run(layoutDetail.layout);
            }
        }

        return this;
    }

    pruneUnlistedStyleIdKeys() {
        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail } = this.layoutData;

        const { style } = layoutDetail;

        Object.keys(style).forEach((key) => {
            if (!this.effectiveLayoutNodes.includes(key)) {
                delete style[key];
            }
        });

        return this;
    }

    pruneUnlistedContentIdKeys() {
        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail } = this.layoutData;

        const { content } = layoutDetail;

        Object.keys(content).forEach((key) => {
            if (!this.effectiveLayoutNodes.includes(key)) {
                delete content[key];
            }
        });

        return this;
    }

    pruneFalsyValueInStyle() {
        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail } = this.layoutData;

        const { style } = layoutDetail;

        Object.entries(style).forEach(([key, value]) => {
            if (isEmpty(value)) {
                delete style[key];
            }

            Object.entries(value).forEach(([featureKey, featureValue]) => {
                if (typeof featureValue === 'boolean' && !featureValue) {
                    delete style[key][featureKey];
                }

                if (typeof featureValue === 'string' && featureValue === '') {
                    delete style[key][featureKey];
                }

                if (typeof featureValue === 'undefined') {
                    delete style[key][featureKey];
                }
            });
        });

        return this;
    }

    removeLegacyKeys() {
        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail } = this.layoutData;

        const legacyKeys = ['feature'];

        legacyKeys.forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(layoutDetail, key)) {
                delete layoutDetail[key];
            }
        });

        return this;
    }

    removeLegacyElementTypeInLayout() {
        const legacyElementType = ['header-icon-tool'];

        const legacyElementIds: string[] = [];

        if (!this.layoutData) {
            return this;
        }

        const { layout: layoutDetail } = this.layoutData;

        const run = (node: LayoutNode) => {
            if (!node || typeof node !== 'object') {
                return;
            }

            if (node.type === 'grid-secondary-level-inner' && node.next) {
                // eslint-disable-next-line no-param-reassign
                node.next = node.next?.filter((next) => {
                    const isLegacyElement = legacyElementType.includes(next.type);

                    if (isLegacyElement) {
                        legacyElementIds.push(next.id);
                    }

                    return !isLegacyElement;
                });

                return;
            }

            if (node.next && Array.isArray(node.next)) {
                node.next.forEach(run);
            }
        };

        if (layoutDetail.layout) {
            if (Array.isArray(layoutDetail.layout)) {
                layoutDetail.layout.forEach(run);
            } else {
                run(layoutDetail.layout);
            }
        }

        if (legacyElementIds.length > 0) {
            legacyElementIds.forEach((id) => {
                delete layoutDetail.style[id];
            });
        }

        return this;
    }

    transformCompatibleData() {
        if (!this.layoutData) {
            return this;
        }

        const legacyElementConfig = [
            {
                sectionName: LAYOUT_SECTION_NAME.FOOTER,
                type: ELEMENT_TYPE.LOGO,
                layout: [
                    {
                        deletable: false,
                    },
                    {
                        deletable: true,
                    },
                ],
            },
        ];

        const { layout: layoutDetail, sectionName: currentSectionName } = this.layoutData;

        const run = (node: LayoutNode) => {
            if (!node || typeof node !== 'object') {
                return;
            }

            if (node.type === 'grid-secondary-level-inner' && node.next) {
                // eslint-disable-next-line no-param-reassign
                node.next?.forEach((next) => {
                    const transformData = legacyElementConfig.find(
                        (config) =>
                            config.sectionName === currentSectionName &&
                            config.type === next.type
                    );

                    if (transformData && transformData.layout) {
                        const [legacyProperties, newProperties] = transformData.layout;

                        Object.entries(newProperties).forEach(([key, value]) => {
                            if (newProperties[key] !== legacyProperties[key]) {
                                // eslint-disable-next-line no-param-reassign
                                next[key] = value;
                            }
                        });
                    }
                });

                return;
            }

            if (node.next && Array.isArray(node.next)) {
                node.next.forEach(run);
            }
        };

        if (layoutDetail.layout) {
            if (Array.isArray(layoutDetail.layout)) {
                layoutDetail.layout.forEach(run);
            } else {
                run(layoutDetail.layout);
            }
        }

        return this;
    }

    getFormattedLayoutData() {
        if (!this.layoutData) {
            return null;
        }

        return this.layoutData;
    }
}
